import { useAddBuyersCertificateConsentMutation } from '@api/buyersCert.api';
import { AddBuyersCertificateConsent, BuyersCertificateConsent } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, HtmlBlock, Title } from '@components';
import { useToast } from '@hooks';
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { colors } from '@themes/colors';
import { t } from '@translations';
import { Controller, useForm } from 'react-hook-form';

type BuyersCertificateConsentFormProps = {
  title?: string;
  successMessage?: string;
  data?: BuyersCertificateConsent['data'];
  token: string;
};
export const BuyersCertificateConsentForm = ({
  title,
  successMessage,
  data,
  token,
}: BuyersCertificateConsentFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    control,
  } = useForm<AddBuyersCertificateConsent>({
    defaultValues: {
      customerName: data?.customerName || '',
      customerAddress: data?.customerAddress || '',
      customerPostCode: data?.customerPostCode || '',
      customerCity: data?.customerCity || '',
      customerEmail: data?.customerEmail || '',
      customerMobile: data?.customerMobile || '',
    },
  });

  const [addConsent, addConsentResponse] = useAddBuyersCertificateConsentMutation();
  const { toast } = useToast();

  const onSubmit = async (formData: AddBuyersCertificateConsent) => {
    try {
      if (data?.id && token) {
        await addConsent({ ...formData, id: data.id, consentToken: token }).unwrap();
      }
    } catch (error) {
      console.error(error);
      toast({
        message: t(TextCodes.BuyersCertificate.Consent.ConsentError),
        severity: 'error',
      });
    }
  };

  if (isSubmitSuccessful) {
    return (
      <HtmlBlock html={successMessage || t(TextCodes.BuyersCertificate.Consent.ConsentSuccess)} />
    );
  }

  return (
    <Box component='form'>
      {title && (
        <Title variant='h2' sx={{ mb: 4, textAlign: 'center' }}>
          {title}
        </Title>
      )}
      <Grid
        container
        spacing={3}
        sx={{
          justifyContent: 'center',
        }}
      >
        <Grid
          item
          xs={12}
          md={8}
          lg={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TextField
            fullWidth
            label={t(TextCodes.BuyersCertificate.Consent.Name)}
            variant='outlined'
            type={'text'}
            sx={{ mt: 5 }}
            {...register('customerName')}
          />
          <TextField
            fullWidth
            label={t(TextCodes.BuyersCertificate.Consent.Address)}
            variant='outlined'
            type={'text'}
            sx={{ mt: 5 }}
            {...register('customerAddress')}
          />
          <Grid container spacing={{ xs: 0, md: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t(TextCodes.BuyersCertificate.Consent.PostCode)}
                variant='outlined'
                type={'text'}
                sx={{ mt: 5 }}
                required
                {...register('customerPostCode', { required: true })}
                error={!!errors.customerPostCode}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label={t(TextCodes.BuyersCertificate.Consent.City)}
                variant='outlined'
                type={'text'}
                sx={{ mt: 5 }}
                {...register('customerCity', { required: true })}
                required
                error={!!errors.customerCity}
              />
            </Grid>
          </Grid>
          <Grid container spacing={{ xs: 0, md: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t(TextCodes.BuyersCertificate.Consent.Email)}
                variant='outlined'
                type={'text'}
                sx={{ mt: 5 }}
                {...register('customerEmail')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t(TextCodes.BuyersCertificate.Block.CustomerMobile)}
                variant='outlined'
                type={'text'}
                sx={{ mt: 5 }}
                required
                {...register('customerMobile', { required: true })}
                error={!!errors.customerMobile}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 2,
            }}
          >
            <Controller
              name='consentGiven'
              control={control}
              defaultValue={false}
              rules={{ required: true }}
              render={({ field }) => (
                <FormGroup
                  sx={{
                    display: 'inline',
                    color: !!errors.consentGiven ? colors.error.main : undefined,
                  }}
                >
                  <FormControlLabel
                    required
                    label={t(TextCodes.BuyersCertificate.Consent.ConsentLabelCheckbox)}
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                  />
                </FormGroup>
              )}
            />
            <Controller
              name='receiveNewsAndUpdates'
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormGroup
                  sx={{
                    display: 'inline',
                  }}
                >
                  <FormControlLabel
                    label={t(TextCodes.BuyersCertificate.Consent.ReceiveUpdatesCheckbox)}
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                  />
                </FormGroup>
              )}
            />
          </Box>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='primary'
              type='submit'
              onClick={handleSubmit(onSubmit)}
              busy={addConsentResponse.isLoading}
              disabled={addConsentResponse.isLoading || isSubmitSuccessful}
            >
              {t(TextCodes.BuyersCertificate.Consent.SubmitConsent)}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
